import { requestRef, type MyRequest, type PersonSummary } from "@scheduler/shared";
import { useCallback, useEffect, useState } from "react";

import { ApiError, useSchedulerApi } from "../api/scheduler";
import { useRefreshOnReturn } from "../refreshOnReturn";
import { NewRequest } from "./NewRequest";
import { RequestList } from "./RequestList";

/** Submit, see status, cancel. */
export function MyRequests({ me }: { me: PersonSummary }) {
  const api = useSchedulerApi();
  const [requests, setRequests] = useState<MyRequest[] | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Bumped after any change, so the new-request panel re-checks against current bookings.
  const [changes, setChanges] = useState(0);

  const refresh = useCallback(async () => {
    try {
      setRequests(await api.myRequests());
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load your requests.");
    }
  }, [api]);

  // Read again quietly on coming back to the tab (punch list F172).
  const back = useRefreshOnReturn();
  useEffect(() => { void refresh(); }, [refresh, back]);

  async function cancel(r: MyRequest) {
    if (!window.confirm(`Cancel ${requestRef(r.label, r.start, r.end)}? It stays on the record as cancelled.`)) return;
    setBusyId(r.id);
    try {
      await api.cancel(r.id);
      setMessage(`${requestRef(r.label, r.start, r.end)} cancelled.`);
      setChanges((n) => n + 1);
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not cancel.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section aria-labelledby="my-requests">
      <h2 id="my-requests" className="panel-title">New request</h2>
      <NewRequest me={me} refreshKey={changes} onSubmitted={(m) => { setMessage(m); setChanges((n) => n + 1); void refresh(); }} />

      <h2 id="my-requests-list" className="panel-title spaced">My requests</h2>
      {message ? <p className="toast" role="status">{message}</p> : null}
      {error ? <p className="error" role="alert">{error}</p> : null}
      {requests ? <RequestList requests={requests} meId={me.id} onCancel={(r) => void cancel(r)} busyId={busyId} /> : <p className="muted">Loading…</p>}
    </section>
  );
}
